package ai.jessie.web.members

import ai.jessie.shared.members.InvitationResponse
import ai.jessie.shared.members.MemberResponse
import ai.jessie.web.onboarding.onboardingProblem
import ai.jessie.web.onboarding.roleLabel
import ai.jessie.web.theme.AlertStyle
import ai.jessie.web.theme.BadgeTone
import ai.jessie.web.theme.ButtonSize
import ai.jessie.web.theme.ButtonVariant
import ai.jessie.web.theme.Grid3Style
import ai.jessie.web.theme.HintStyle
import ai.jessie.web.theme.MemberActionsStyle
import ai.jessie.web.theme.MemberAvatarStyle
import ai.jessie.web.theme.MemberCardStyle
import ai.jessie.web.theme.MemberGridStyle
import ai.jessie.web.theme.MemberHeadStyle
import ai.jessie.web.theme.MemberNameStyle
import ai.jessie.web.theme.MutedStyle
import ai.jessie.web.theme.PendingAvatarStyle
import ai.jessie.web.theme.PendingCardStyle
import ai.jessie.web.theme.RowStyle
import ai.jessie.web.theme.SmallStyle
import ai.jessie.web.theme.StackStyle
import ai.jessie.web.theme.StatusStyle
import ai.jessie.web.theme.TableStrongStyle
import ai.jessie.web.theme.badge
import ai.jessie.web.theme.card
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.silk.style.toAttrs
import kotlinx.browser.document
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.B
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import org.w3c.dom.HTMLElement

// UC-005 (`docs/design/screens/20-EinstellungenTeam.png`): member and invitation cards, the
// invitation form and the roles explainer. The page title and side navigation live in the page.
@Composable
fun memberManagement(
    organisationId: String,
    state: MemberManagementUiState,
    actions: MemberManagementActions,
) {
    val focusReturn = remember { FocusReturn() }
    state.error?.let { Div(attrs = AlertStyle.toAttrs()) { onboardingProblem(it, state.busy) } }
    if (state.error?.authenticationRequired != true) {
        if (state.busy) status("Wird geladen oder gespeichert …")
        state.completed?.let { status(it.completedText()) }
        val pending = state.pending
        when {
            state.authorityEnded ->
                P(attrs = HintStyle.toAttrs()) {
                    Text(
                        "Du bist in dieser Organisation nicht mehr Eigentümer. " +
                            "Die Mitgliederverwaltung wird nicht mehr angezeigt."
                    )
                }
            pending != null -> memberChangeConfirmation(pending, state.busy, actions, focusReturn)
            else -> {
                state.management?.let {
                    memberList(it.members, state, actions, focusReturn)
                    invitationList(it.invitations, state, actions)
                    invitationForm(state, actions)
                    rolesCard()
                }
                Div(attrs = RowStyle.toAttrs()) {
                    themedButton(
                        "Mitglieder erneut laden",
                        actions.refresh,
                        ButtonVariant.GHOST,
                        disabled = state.busy,
                    )
                }
            }
        }
    }
    P { A(href = "/organisations/$organisationId") { Text("Zurück zur Organisation") } }
}

@Composable
private fun status(text: String) {
    P(attrs = StatusStyle.toAttrs { attr("role", "status") }) { Text(text) }
}

@Composable
private fun memberList(
    members: List<MemberResponse>,
    state: MemberManagementUiState,
    actions: MemberManagementActions,
    focusReturn: FocusReturn,
) {
    val busy = state.busy
    LaunchedEffect(Unit) {
        // While a problem is shown, the alert or the marked field keeps focus.
        if (focusReturn.pending && state.error == null && !AppGlobals.isExporting)
            (document.getElementById(MEMBERS_TITLE_ID) as? HTMLElement)?.focus()
        focusReturn.pending = false
    }
    Section(attrs = StackStyle.toAttrs { attr("aria-labelledby", MEMBERS_TITLE_ID) }) {
        H2(
            attrs = {
                id(MEMBERS_TITLE_ID)
                attr("tabindex", "-1")
            }
        ) {
            Text("Mitglieder")
        }
        Ul(attrs = MemberGridStyle.toAttrs()) {
            members.forEach { member ->
                Li(attrs = MemberCardStyle.toAttrs()) {
                    val name = member.displayName ?: member.email.orEmpty()
                    personHead(
                        initials(name),
                        pending = false,
                        name,
                        member.email.takeIf { member.displayName != null },
                    ) {
                        badge(roleLabel(member.role), roleTone(member.role))
                        if (member.self) badge("du")
                        if (member.status == "SUSPENDED") badge("gesperrt", BadgeTone.DANGER)
                    }
                    cardActions {
                        MEMBER_ROLES.filter { it != member.role }
                            .forEach { role ->
                                cardAction(
                                    "${member.name()}: Rolle zu ${roleLabel(role)} ändern",
                                    busy,
                                ) {
                                    actions.choose(MemberChange.ChangeRole(member, role))
                                }
                            }
                        cardAction("${member.name()} entfernen", busy) {
                            actions.choose(MemberChange.Remove(member))
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun invitationList(
    invitations: List<InvitationResponse>,
    state: MemberManagementUiState,
    actions: MemberManagementActions,
) {
    Section(attrs = StackStyle.toAttrs { attr("aria-labelledby", "invitations-title") }) {
        H2(attrs = { id("invitations-title") }) { Text("Offene Einladungen") }
        if (invitations.isEmpty())
            P(attrs = HintStyle.toAttrs()) { Text("Keine offenen Einladungen.") }
        Ul(attrs = MemberGridStyle.toAttrs()) {
            invitations.forEach { invitation ->
                Li(attrs = PendingCardStyle.toAttrs()) {
                    if (invitation.invitationId == state.existingInvitationId)
                        Div { B { Text("Bereits eingeladen:") } }
                    personHead(
                        "?",
                        pending = true,
                        if (invitation.expired) "Einladung abgelaufen" else "Einladung ausstehend",
                        invitation.email,
                    ) {
                        badge(roleLabel(invitation.role), roleTone(invitation.role))
                    }
                    Div(attrs = listOf(SmallStyle, MutedStyle).toAttrs()) {
                        Div { Text(invitation.validity()) }
                        // BR-026: only the recorded outcome; never "sent" or "received" beyond it.
                        Div { Text(emailOutcomeText(invitation.emailOutcome)) }
                    }
                    cardActions {
                        cardAction("Einladung an ${invitation.email} erneut senden", state.busy) {
                            actions.choose(MemberChange.Resend(invitation))
                        }
                        cardAction("Einladung an ${invitation.email} widerrufen", state.busy) {
                            actions.choose(MemberChange.Revoke(invitation))
                        }
                    }
                }
            }
        }
    }
}

// Avatar, name over the muted e-mail, badges on the right. The initials repeat the name, so the
// avatar is hidden from assistive technology.
@Composable
private fun personHead(
    initials: String,
    pending: Boolean,
    title: String,
    detail: String?,
    badges: @Composable () -> Unit,
) {
    Div(attrs = MemberHeadStyle.toAttrs()) {
        val avatar = if (pending) PendingAvatarStyle else MemberAvatarStyle
        Span(attrs = avatar.toAttrs { attr("aria-hidden", "true") }) { Text(initials) }
        Div(attrs = MemberNameStyle.toAttrs()) {
            Span(attrs = TableStrongStyle.toAttrs()) { Text(title) }
            detail?.let { Span(attrs = listOf(SmallStyle, MutedStyle).toAttrs()) { Text(it) } }
        }
        Div(attrs = RowStyle.toAttrs()) { badges() }
    }
}

// The artboard's kebab menu has no component in the catalogue; the existing actions stay visible.
@Composable
private fun cardActions(content: @Composable () -> Unit) {
    Div(attrs = MemberActionsStyle.toAttrs()) { content() }
}

@Composable
private fun cardAction(label: String, busy: Boolean, action: () -> Unit) {
    themedButton(label, action, size = ButtonSize.SMALL, disabled = busy)
}

// Static explanation of the three roles, from the actor table in `docs/requirements.md`.
@Composable
private fun rolesCard() {
    card("Rollen", "Die Rolle bestimmt, was jemand in dieser Organisation sehen und ändern darf.") {
        Div(attrs = Grid3Style.toAttrs()) {
            roleExplanations.forEach { (role, text) ->
                Div(attrs = StackStyle.toAttrs()) {
                    Span(attrs = TableStrongStyle.toAttrs()) { Text(roleLabel(role)) }
                    Span { Text(text) }
                }
            }
        }
    }
}

private val roleExplanations =
    listOf(
        "OWNER" to "Verwaltet Organisation, Mitglieder, Abrechnung und alle Betriebsdaten.",
        "STANDARD" to "Erfasst Stunden und bearbeitet Betriebsdaten der Organisation.",
        "ACCOUNTANT" to
            "Liest Finanz- und Kundendaten und bearbeitet buchhaltungsrelevante Dokumente.",
    )

private fun roleTone(role: String) =
    when (role) {
        "OWNER" -> BadgeTone.PRIMARY
        "ACCOUNTANT" -> BadgeTone.ACCENT
        else -> BadgeTone.NEUTRAL
    }

private fun initials(name: String) =
    name
        .split(' ', '.', '@')
        .filter { it.isNotBlank() }
        .take(2)
        .joinToString("") { it.first().uppercase() }

private fun InvitationResponse.validity() =
    if (expired) "abgelaufen am ${localTime(expiresAt)}" else "gültig bis ${localTime(expiresAt)}"

internal const val MEMBERS_TITLE_ID = "members-title"
